/**
 * Разбор звука с микрофона: что можно сказать человеку честно.
 *
 * Отдельно от компонента, потому что это единственная часть инструмента,
 * которую можно проверить без браузера и без живого микрофона. Компонент
 * получает звук, этот модуль превращает числа в приговор.
 *
 * Важное ограничение всего замысла: мы измеряем сигнал **на входе в
 * браузер**. Что услышат в Zoom или Discord, зависит ещё и от их
 * собственной обработки, которой мы не видим. Поэтому нигде не обещается
 * «так вас слышат другие»: обещается «так звучит ваш микрофон».
 */

/** Тишина: значение, ниже которого логарифм считать бессмысленно. */
const SILENCE = 1e-7;

/**
 * Громкость в dBFS.
 *
 * Ноль — предел, при котором отсчёты упираются в потолок и звук режется.
 * Всё остальное отрицательное: -20 громко, -60 почти тишина.
 */
export function toDbfs(amplitude: number): number {
  if (amplitude <= SILENCE) return -Infinity;
  return 20 * Math.log10(Math.min(amplitude, 1));
}

/** Пиковая и средняя громкость куска звука. */
export function levels(samples: Float32Array): { peak: number; rms: number } {
  let peak = 0;
  let sum = 0;

  for (const value of samples) {
    const abs = Math.abs(value);
    if (abs > peak) peak = abs;
    sum += value * value;
  }

  const rms = samples.length > 0 ? Math.sqrt(sum / samples.length) : 0;
  return { peak, rms };
}

/**
 * Доля отсчётов, упёршихся в потолок.
 *
 * Порог 0.99, а не 1.0: оцифровка даёт значения чуть ниже единицы даже
 * при явном клиппинге, и точное сравнение не поймало бы ничего.
 */
export function clipping(samples: Float32Array): number {
  if (samples.length === 0) return 0;

  let clipped = 0;
  for (const value of samples) {
    if (Math.abs(value) >= 0.99) clipped += 1;
  }

  return clipped / samples.length;
}

/** Приговор по громкости речи. */
export type LoudnessVerdict = 'silent' | 'quiet' | 'good' | 'loud' | 'clipping';

/**
 * Оценка громкости речи по пику в dBFS.
 *
 * Границы взяты из практики звукозаписи, а не придуманы: речь считают
 * нормальной, когда пики лежат в районе -12…-6 dBFS. Ниже -30 собеседник
 * начинает переспрашивать, выше -3 звук режется.
 */
export function loudnessVerdict(peakDbfs: number, clipRatio: number): LoudnessVerdict {
  // Клиппинг важнее уровня: звук уже испорчен, и «громко» тут мало
  // сказать. Доля в одну тысячную уже слышна на речи как треск.
  if (clipRatio >= 0.001) return 'clipping';
  if (peakDbfs === -Infinity || peakDbfs < -50) return 'silent';
  if (peakDbfs < -30) return 'quiet';
  if (peakDbfs > -6) return 'loud';
  return 'good';
}

/** Приговор по фоновому шуму комнаты. */
export type NoiseVerdict = 'quiet' | 'noticeable' | 'loud';

/**
 * Оценка фона в тишине.
 *
 * Меряется по средней громкости, а не по пику: одиночный стук — это не
 * фон комнаты, а средняя величина как раз показывает постоянный гул
 * вентилятора, кондиционера или улицы за окном.
 */
export function noiseVerdict(noiseDbfs: number): NoiseVerdict {
  if (noiseDbfs === -Infinity || noiseDbfs < -60) return 'quiet';
  if (noiseDbfs < -45) return 'noticeable';
  return 'loud';
}

/**
 * Запас между речью и шумом.
 *
 * Самое полезное число во всём разборе, и именно его не показывает ни
 * один конкурент. Громкая речь в тихой комнате и та же речь при
 * работающем кондиционере звучат совершенно по-разному, а «уровень
 * сигнала» у них одинаковый.
 */
export function signalToNoise(peakDbfs: number, noiseDbfs: number): number {
  if (peakDbfs === -Infinity) return 0;
  if (noiseDbfs === -Infinity) return Infinity;
  return peakDbfs - noiseDbfs;
}

/** Оценка запаса: ниже 20 дБ собеседник слышит комнату наравне с речью. */
export function snrVerdict(snr: number): 'poor' | 'fair' | 'good' {
  if (snr < 15) return 'poor';
  if (snr < 25) return 'fair';
  return 'good';
}

/** Что именно не так с микрофоном: набор замечаний для человека. */
export interface MicReport {
  peakDbfs: number;
  rmsDbfs: number;
  noiseDbfs: number;
  clipRatio: number;
  snr: number;
  loudness: LoudnessVerdict;
  noise: NoiseVerdict;
  quality: 'poor' | 'fair' | 'good';
}

/** Собирает разбор из замеров речи и тишины. */
export function buildReport(speech: Float32Array, silence: Float32Array): MicReport {
  const speechLevels = levels(speech);
  const silenceLevels = levels(silence);

  const peakDbfs = toDbfs(speechLevels.peak);
  const rmsDbfs = toDbfs(speechLevels.rms);
  const noiseDbfs = toDbfs(silenceLevels.rms);
  const clipRatio = clipping(speech);
  const snr = signalToNoise(peakDbfs, noiseDbfs);

  return {
    peakDbfs,
    rmsDbfs,
    noiseDbfs,
    clipRatio,
    snr,
    loudness: loudnessVerdict(peakDbfs, clipRatio),
    noise: noiseVerdict(noiseDbfs),
    quality: snrVerdict(snr),
  };
}

/** Причина, по которой микрофон недоступен. */
export type MicDenial = 'denied' | 'missing' | 'busy' | 'insecure' | 'unsupported' | 'unknown';

/**
 * Разбор отказа в доступе.
 *
 * Это главный сценарий, а не крайний: человек приходит сюда именно
 * потому, что микрофон не работает, и у него отказ вероятнее, чем у
 * прочих. Вечное ожидание вместо объяснения — худшее, что можно ему
 * показать.
 *
 * Имена ошибок берутся из спецификации, а не выдумываются: у каждого
 * браузера они одни и те же, различается только текст сообщения.
 */
export function denialReason(error: unknown): MicDenial {
  if (typeof error !== 'object' || error === null) return 'unknown';

  const name = 'name' in error ? String(error.name) : '';

  switch (name) {
    // Человек нажал «Запретить» или запрет стоит в настройках сайта.
    case 'NotAllowedError':
    case 'SecurityError':
      return 'denied';
    // Микрофона нет вовсе: ни встроенного, ни подключённого.
    //
    // NotSupportedError сюда же: так отвечает Chromium, когда в
    // системе нет ни одного устройства записи. Отнести его к
    // «неизвестно» значит посоветовать человеку без микрофона
    // проверить настройки звука вместо того, чтобы сказать прямо.
    case 'NotFoundError':
    case 'OverconstrainedError':
    case 'NotSupportedError':
      return 'missing';
    // Устройство занято другой программой. На Windows это обычное дело:
    // Zoom или Skype держат микрофон монопольно.
    case 'NotReadableError':
    case 'AbortError':
      return 'busy';
    default:
      return 'unknown';
  }
}
